import moment from "moment";

/**
 * Normalizadores de documentos de usuario.
 *
 * Los datos llegan de colecciones distintas (`app_usuarios` desde la app,
 * `usuarios`/`comercios`/`empresas` heredadas) y cada una nombró los mismos
 * campos a su manera. Estas funciones son el único lugar que conoce esos alias.
 */

export function pick(obj, keys) {
  for (const k of keys) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k) && obj[k]) return obj[k];
  }
  return undefined;
}

export function looksLikeEmail(v) {
  if (typeof v !== "string") return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

function findEmailDeep(obj, depth = 0) {
  if (!obj || depth > 3) return null;
  if (typeof obj === "string" && looksLikeEmail(obj)) return obj.trim().toLowerCase();
  if (typeof obj !== "object") return null;

  const direct = pick(obj, EMAIL_KEYS);
  if (looksLikeEmail(direct)) return direct.trim().toLowerCase();

  for (const key of Object.keys(obj)) {
    const found = findEmailDeep(obj[key], depth + 1);
    if (found) return found;
  }
  return null;
}

const EMAIL_KEYS = [
  "email", "correo", "mail", "correoUsuario", "Email", "emailEmpresa", "company_email",
];

export function extractEmail(u) {
  const direct = pick(u, EMAIL_KEYS);
  if (looksLikeEmail(direct)) return direct.trim().toLowerCase();
  return findEmailDeep(u) || "";
}

export function extractStripeId(u) {
  const v = pick(u, [
    "stripe_id", "stripeId", "stripeCustomerId", "customer_id",
    "stripeCustomer", "customerId", "stripe_customer_id", "stripeCustomerID",
  ]);
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

export function extractName(u) {
  const direct = pick(u, [
    "name", "nombre", "fullName", "displayName",
    "nombreEmpresa", "razonSocial", "companyName", "empresa", "comercio", "nombre_comercio",
  ]);
  if (direct) return String(direct);

  const fn = pick(u, ["firstName", "nombre1", "primerNombre"]);
  const ln = pick(u, ["lastName", "apellido", "apellido1", "primerApellido"]);
  if (fn || ln) return [fn, ln].filter(Boolean).join(" ");
  return "";
}

export function extractPhone(u) {
  const v = pick(u, [
    "phone", "telefono", "celular", "phoneNumber", "tel",
    "telefonoEmpresa", "phoneCompany",
  ]);
  return v ? String(v) : "";
}

export function extractOwnReferralCode(u) {
  const c = pick(u, [
    "referral_code", "referralCode", "codigoReferido", "codigo_referido",
    "referal_code", "refCode", "ref_code", "refcode",
  ]);
  return c ? String(c).trim() : "";
}

export function extractUsedReferralCode(u) {
  const used = pick(u, [
    "referred_by", "used_referral_code", "referred_by_code", "referrer_code",
    "invitadoPorCodigo", "invited_by_code", "referral_parent_code",
    "cameFromCode", "came_from_code", "referredBy", "referred_by_company",
  ]);
  return used ? String(used).trim() : "";
}

/**
 * Convierte a `moment` cualquiera de los formatos de fecha que conviven en la
 * base: la app guarda `creation_timestamp` como epoch en milisegundos (string)
 * y `register_date` como "YYYY-MM-DD HH:mm:ss", mientras que el admin guarda
 * "DD-MM-YYYY". Devuelve null si no se puede interpretar.
 */
export function parseFechaFlexible(valor) {
  if (valor === null || valor === undefined || valor === "") return null;

  // Timestamp de Firestore
  if (typeof valor.toDate === "function") return moment(valor.toDate());

  if (typeof valor === "number") {
    // Los epoch en segundos caben en 10 dígitos; los de ms, en 13.
    return moment(valor < 2e10 ? valor * 1000 : valor);
  }

  if (typeof valor === "string") {
    const s = valor.trim();
    if (/^\d+$/.test(s)) return parseFechaFlexible(Number(s));

    const m = moment(s, [
      moment.ISO_8601,
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD",
      "DD-MM-YYYY HH:mm:ss",
      "DD-MM-YYYY",
      "DD/MM/YYYY",
    ], true);
    return m.isValid() ? m : null;
  }

  return null;
}

/** Fecha de creación del usuario, normalizada. */
export function extractCreatedAt(u) {
  const v = pick(u, [
    "creation_timestamp", "createdAt", "creadoEn", "fechaCreacion",
    "created_on", "created_at", "creado_el",
  ]);
  return parseFechaFlexible(v);
}

export function formatFecha(m, formato = "DD/MM/YYYY") {
  return m && m.isValid() ? m.format(formato) : "—";
}

/**
 * Tipo de usuario tal como lo escribe la app: UserForm pone "normal" o
 * "comercio" y SocioComercial siempre "comercio". Los registros viejos no
 * traen el campo.
 */
export function extractTipo(u) {
  const t = String((u && u.type) || "").toLowerCase();
  if (t === "comercio") return "comercio";
  if (t === "normal" || t === "user") return "usuario";
  return "sin_definir";
}

export function etiquetaTipo(tipo) {
  if (tipo === "comercio") return "Comercio";
  if (tipo === "usuario") return "Usuario";
  return "Sin definir";
}

/**
 * Estado del alta: la app marca "signup_pending" al crear la cuenta y
 * "signup_completed" cuando el usuario termina su perfil.
 */
export function extractEstadoRegistro(u) {
  const s = String((u && u.status) || "").toLowerCase();
  if (s === "signup_completed") return "completado";
  if (s === "signup_pending") return "pendiente";
  return "sin_estado";
}

/** Método de alta: "email" o "google". */
export function extractMetodoAlta(u) {
  const s = String((u && u.signin_method) || "").toLowerCase();
  return s === "google" || s === "email" ? s : "sin_definir";
}

export function extractCiudad(u) {
  const c = pick(u, ["city", "ciudad"]);
  if (!c) return "Sin ciudad";
  const s = String(c).trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "Sin ciudad";
}

/**
 * Deja un documento crudo en la forma que usan todas las vistas del admin.
 */
export function normalizarUsuario(doc) {
  const email = extractEmail(doc);
  const creadoEn = extractCreatedAt(doc);
  return {
    ...doc,
    email,
    name: extractName(doc) || email,
    phone: extractPhone(doc),
    tipo: extractTipo(doc),
    estadoRegistro: extractEstadoRegistro(doc),
    metodoAlta: extractMetodoAlta(doc),
    ciudad: extractCiudad(doc),
    referral_code: extractOwnReferralCode(doc),
    used_referral_code: extractUsedReferralCode(doc).toLowerCase(),
    creadoEn,
    creadoEnFormatted: formatFecha(creadoEn),
  };
}
